import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AiFillGithub, AiFillEye, AiOutlineClose } from "react-icons/ai";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import "./ProjectModal.scss";

const ProjectModal = ({ project, isOpen, onClose }) => {
  const [currentScreenshotIndex, setCurrentScreenshotIndex] = useState(0);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && project?.screenshots) {
      setCurrentScreenshotIndex(0);
    }
  }, [isOpen, project]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      window.addEventListener("keydown", handleEscape);
      return () => window.removeEventListener("keydown", handleEscape);
    }
  }, [isOpen, onClose]);

  if (!project) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="project-modal-backdrop"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="project-modal"
          >
            {/* Header */}
            <div className="project-modal-header">
              <h1 className="project-modal-title-header">{project.title}</h1>
              <button
                className="project-modal-close-btn"
                onClick={onClose}
                aria-label="Close modal"
              >
                <AiOutlineClose />
              </button>
            </div>

            {/* Content */}
            <div className="project-modal-content">
              {/* Body */}
              <div className="project-modal-body">
                {/* Description */}
                <div className="project-modal-section">
                  <h2 className="project-modal-section-title">About</h2>
                  <p className="project-modal-description">{project.description}</p>
                </div>

                {/* Technologies */}
                {project.technologies && project.technologies.length > 0 && (
                  <div className="project-modal-section">
                    <h2 className="project-modal-section-title">Technologies</h2>
                    <div className="project-modal-tech">
                      {project.technologies.map((tech, index) => (
                        <span key={index} className="project-modal-tech-tag">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Screenshots */}
                {project.screenshots && project.screenshots.length > 0 && (
                  <div className="project-modal-section">
                    <h2 className="project-modal-section-title">Screenshots</h2>
                    <div className="project-modal-screenshots-carousel">
                      <div className="project-modal-screenshot-container">
                        <AnimatePresence mode="wait">
                          <motion.img
                            key={currentScreenshotIndex}
                            src={project.screenshots[currentScreenshotIndex]}
                            alt={`${project.title} screenshot ${currentScreenshotIndex + 1}`}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3 }}
                            className="project-modal-screenshot-main"
                          />
                        </AnimatePresence>
                        {project.screenshots.length > 1 && (
                          <>
                            <button
                              className="project-modal-carousel-btn project-modal-carousel-prev"
                              onClick={() =>
                                setCurrentScreenshotIndex(
                                  (prev) => (prev - 1 + project.screenshots.length) % project.screenshots.length
                                )
                              }
                              aria-label="Previous screenshot"
                            >
                              <IoChevronBack />
                            </button>
                            <button
                              className="project-modal-carousel-btn project-modal-carousel-next"
                              onClick={() =>
                                setCurrentScreenshotIndex(
                                  (prev) => (prev + 1) % project.screenshots.length
                                )
                              }
                              aria-label="Next screenshot"
                            >
                              <IoChevronForward />
                            </button>
                            <div className="project-modal-carousel-indicators">
                              {project.screenshots.map((_, index) => (
                                <button
                                  key={index}
                                  className={`project-modal-carousel-indicator ${
                                    index === currentScreenshotIndex ? "active" : ""
                                  }`}
                                  onClick={() => setCurrentScreenshotIndex(index)}
                                  aria-label={`Go to screenshot ${index + 1}`}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                      {project.screenshots.length > 1 && (
                        <div className="project-modal-thumbnails">
                          {project.screenshots.map((screenshot, index) => {
                            const isPrev = index === (currentScreenshotIndex - 1 + project.screenshots.length) % project.screenshots.length;
                            const isCurrent = index === currentScreenshotIndex;
                            const isNext = index === (currentScreenshotIndex + 1) % project.screenshots.length;
                            
                            if (!isPrev && !isCurrent && !isNext) return null;

                            return (
                              <div
                                key={index}
                                className={`project-modal-thumbnail ${
                                  isCurrent ? "active" : ""
                                }`}
                                onClick={() => setCurrentScreenshotIndex(index)}
                              >
                                <img
                                  src={screenshot}
                                  alt={`${project.title} thumbnail ${index + 1}`}
                                />
                                {isPrev && <span className="project-modal-thumbnail-label">Prev</span>}
                                {isNext && <span className="project-modal-thumbnail-label">Next</span>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Links */}
                <div className="project-modal-section">
                  <h2 className="project-modal-section-title">Links</h2>
                  <div className="project-modal-links">
                    {project.projectLink && (
                      <a
                        href={project.projectLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-modal-link project-modal-link-primary"
                      >
                        <AiFillEye />
                        <span>Live Demo</span>
                      </a>
                    )}
                    {project.codeLink && (
                      <a
                        href={project.codeLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-modal-link project-modal-link-secondary"
                      >
                        <AiFillGithub />
                        <span>View Code</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;

